// Texterna i testläget: orsakerna till granskning och beloppen i resultatet.
import type { components } from '../api/schema.js';
import { kr, type ServiceCatalog } from '../katalog/sammanfattning.js';
import { BOLAGSFORMER, drivare } from '../katalog/texter.js';

export type Testresultat = components['schemas']['TestResult'];
export type Berakning = NonNullable<Testresultat['calculation']>;
type Belopp = { low: number; high: number };

const PERIOD = { monthly: '/mån', yearly: '/år', once: '' } as const;

/** "1 500 kr" eller "2 400–2 900 kr", med periodens ändelse. */
export function beloppText(b: Belopp, billing?: keyof typeof PERIOD): string {
  const slut = billing ? PERIOD[billing] : '';
  if (b.low === b.high) return `${kr(b.low)}${slut}`;
  return `${kr(b.low).replace(' kr', '')}–${kr(b.high)}${slut}`;
}

const tjanst = (katalog: ServiceCatalog | undefined, id: unknown) =>
  katalog?.services.find((s) => s.id === id)?.label ?? String(id);

const bolagsform = (kod: unknown) => BOLAGSFORMER.find((b) => b.kod === kod)?.namn ?? String(kod);

/** Orsaken till granskning i klartext. Okända koder visas som de är. */
export function orsakText(
  orsak: { code: string; [k: string]: unknown },
  katalog?: ServiceCatalog,
): string {
  const tal = (v: unknown) => (typeof v === 'number' ? v : 0);
  switch (orsak.code) {
    case 'interpretationInvalid':
      return 'AI:s svar gick inte att använda, inte heller efter ett nytt försök.';
    case 'interpretationTimedOut':
      return `AI svarade inte inom ${Math.round(tal(orsak.timeoutMs) / 1000)} sekunder.`;
    case 'interpretationFailed':
      return 'AI gick inte att nå.';
    case 'missingLegalForm':
      return 'Bolagsformen saknas i förfrågan.';
    case 'lowConfidence':
      return `AI var säker till ${tal(orsak.confidencePercent)} %, under er gräns på ${tal(orsak.thresholdPercent)} %.`;
    case 'flagged': {
      const flaggor = Array.isArray(orsak.flags) ? (orsak.flags as { code: string }[]) : [];
      return `Förfrågan nämner ${flaggor.map((f) => f.code).join(', ')}, som ni alltid vill granska.`;
    }
    case 'driverAboveHighestTier':
      return `${tjanst(katalog, orsak.serviceId)}: ${tal(orsak.value)} ${drivare(String(orsak.driver)).kort} är över trappans högsta steg, ${tal(orsak.highestTier)}.`;
    case 'noPriceForLegalForm':
      return `${tjanst(katalog, orsak.serviceId)} saknar pris för ${bolagsform(orsak.legalForm).toLowerCase()}.`;
    case 'monthlyTotalAboveLimit':
      return `Månadssumman ${kr(tal(orsak.monthlyTotal))} är över er gräns på ${kr(tal(orsak.limit))}.`;
    case 'noLines':
      return 'AI valde inga tjänster.';
    default:
      return orsak.code;
  }
}
